import { useQuery } from "@tanstack/react-query";

import { useUser } from "@/contexts/UserContext";
import { AZURE_MJ } from "@/lib/axios";

export const BUSINESS_CENTRAL_QUERY_KEYS = {
  getProjects: "BUSINESS_CENTRAL_GET_PROJECTS"
};

export interface IProject {
  "@odata.etag": string;
  id: string;
  systemId: string;
  no: string;
  number: string;
  displayName: string | null;
  description: string;
  status: string;

  projectManagerNumber: string | null;
  projectManager: string;
  personResponsible: string;

  billToCustomerNumber: string | null;
  billToCustomerNo: string;
  billToCustomerName: string | null;
  billToName: string;
  billToAddress: string;
  billToAddress2: string;
  billToCity: string;
  billToCounty: string;
  billToPostCode: string;
  billToCountryRegionCode: string;
  billToContact: string;
  billToContactNo: string;

  sellToCustomerNumber: string | null;
  sellToCustomerNo: string;
  sellToCustomerName: string;
  shipToName: string;
  shipToAddress: string;
  shipToAddress2: string;
  shipToCity: string;
  shipToCounty: string;
  shipToPostCode: string;
  shipToCountryRegionCode: string;
  sellToContact: string;
  sellToContactNo: string;

  currencyCode: string;
  invoiceCurrencyCode: string;

  startingDate: string; // ISO date
  endingDate: string; // ISO date
  createdDate: string | null;
  creationDate: string; // ISO date
  lastDateModified: string; // ISO date

  percentageOfCompletion: number | null;
  completedPercentage: number | null;
  invoicedPercentage: number | null;
  recognizedCosts: number | null;
  recognizedRevenue: number | null;
  totalCosts: number | null;
  totalSalesAmount: number | null;

  blocked: boolean;

  globalDimension1Code: string;
  globalDimension2Code: string;
  jobPostingGroup: string;
  wipMethod: string;
  languageCode: string;
  externalDocumentNo: string;
  yourReference: string;
  reserve: string;
  noSeries: string;

  budgetTotalCost: number | null;

  opportunityNo?: string | null;
}

type ProjectsResponse = {
  "@odata.context": string;
  value: IProject[];
};

// `hasOpportunity=true` returns one row per (project × opportunity), so a
// project with more than one opportunity comes back multiple times sharing the
// same `no`/`description`. The project pickers use `no`/`description` as the
// Select option value, and Ant Design requires those to be unique — duplicates
// make the dropdowns misbehave (selecting one highlights all matches). Dedupe
// by `no` (the BC primary key) once, here, so every consumer gets a clean list
// instead of each dropdown re-deduping. Defined at module scope so React
// Query's `select` keeps a stable reference and only re-runs when data changes.
// const dedupeProjectsByNo = (
//   data: ProjectsResponse
// ): ProjectsResponse => {
//   const seen = new Set<string>();
//   const value = data.value.filter((project) => {
//     if (!project.description) return true; // no primary key to dedupe on — keep it
//     if (seen.has(project.description)) return false;
//     seen.add(project.description);
//     return true;
//   });
//   return { ...data, value };
// };

// Projects without a non-empty opportunityNo are hidden from users across the
// whole app (dashboards, pickers, approvals, etc.), so the filter lives here
// instead of being repeated at every call site.
/**
 * `regionCode` defaults to the signed-in user's region. Pass `null` to fetch
 * every region — the `region` param is then left off the request entirely.
 */
export const useProjects = (regionCode?: string | null) => {
  const user = useUser();
  const region =
    regionCode === undefined ? user?.regionCode : (regionCode ?? undefined);

  return useQuery({
    enabled: !!user,
    // Region is part of the key so changing it fetches that region's list.
    queryKey: [BUSINESS_CENTRAL_QUERY_KEYS.getProjects, region ?? "all"],
    queryFn: async (): Promise<ProjectsResponse> => {
      const { data } = await AZURE_MJ.get(`/bc/projects`, {
        params: {
          hasOpportunity: true,
          region: region || undefined
        }
      });
      return data;
    },
    // select: dedupeProjectsByNo,
    staleTime: 5 * 60 * 1000
  });
};
